/**
 * The "Basic" form variant is characterized by:
 *    - 3px rounded corners
 *    - 30px input height
 *    - 11px text
 *    - borders all around text inputs
 *    - solid blue primary buttons
 */

import React, { forwardRef } from 'react'
import { Key } from '@web/lib/keyboard'
import * as SafeRecord from '@web/lib/safe_record'
import * as stylex from '@web/lib/stylex'
import { asTrackedClickable } from '@web/o11y/analytics/tracked_clickable'
import { asTrackedInput } from '@web/o11y/analytics/tracked_input'
import { InputPopulationLevel } from '@web/o11y/analytics/tracking'
import infoIcon from '@web/ui-platform/images/info_small.svg'
import { Svg } from '@web/ui-platform/svg_deprecated'
import { getTooltipProps, TooltipType } from '@web/ui-platform/tooltip'
import classNames from 'classnames'

import { tryOpenUrlForIntegration } from 'lib/integration'
import { LazyInput__DEPRECATED } from 'shared/components/lazy_input/lazy_input'
import { sx_DEPRECATED } from 'shared/style_builder'

import { Link as BaseLink, type LinkProps } from './link'

import * as styles from './basic_form.css'

/**
 * We do this instead of importing forwardInputKeyToFullscreenIfNecessary, because
 * this is a shared component, and we don't want to import anything from the figma_app
 * bundle into shared.
 */
let forwardInputKeyToFullscreenIfNecessary:
  ((e: React.KeyboardEvent<HTMLElement>) => boolean) | null = null
export function setForwardInputKeyToFullscreenIfNecessary(
  fn: (e: React.KeyboardEvent<HTMLElement>) => boolean,
) {
  forwardInputKeyToFullscreenIfNecessary = fn
}

interface ButtonGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  reversed?: boolean
}

/**
 * Displays buttons in a horizontal row. Ensure that the content represents the order
 * by which a user can tab through available options (the `reversed` props allows for
 * visually displaying the items in a different order, e.g. Cancel on the left, Confirm
 * on the right)
 *
 * A note that this function does not control spacing between individual buttons as of
 * 11/2021, but can adopt patterns similar to other .buttonGroup elements elsewhere
 */
export function ButtonGroup(props: ButtonGroupProps) {
  return (
    <div
      className={classNames(
        styles.buttonGroup,
        props.reversed && styles.buttonGroup_reversed,
        props.className,
      )}
    >
      {props.children}
    </div>
  )
}

interface ButtonBaseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  defaultClass: string
  dataTestId?: string
  forwardedRef?: React.Ref<HTMLButtonElement>
}

export class ButtonBase extends SafeRecord.Component<ButtonBaseProps> {
  public static displayName = 'ButtonBase'

  private onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (forwardInputKeyToFullscreenIfNecessary?.(e)) return

    this.props.onKeyDown?.(e)

    if (e.keyCode === Key.ESCAPE) {
      ;(e.target as HTMLButtonElement).blur()
    }
  }

  private onClick = SafeRecord.mouseEvent(
    this,
    'click',
    (e: React.MouseEvent<HTMLButtonElement>) => {
      this.props.onClick?.(e)
    },
  )

  private onPointerDown = SafeRecord.pointerEvent(
    this,
    'pointerdown',
    (e: React.PointerEvent<HTMLButtonElement>) => {
      this.props.onPointerDown?.(e)
    },
  )

  public override render() {
    const { forwardedRef, ...propsCopy } = this.props
    propsCopy.className = `${this.props.defaultClass} ${this.props.className || ''}`
    const dataTestId = this.props.dataTestId
    // FIXME: Ignored for TS 4.5 migration
    // @ts-expect-error
    delete propsCopy.defaultClass
    delete propsCopy.recordingKey
    delete propsCopy.dataTestId

    if (this.props.onClick && this.props.recordingKey) {
      propsCopy.onClick = this.onClick
    }

    if (this.props.onPointerDown && this.props.recordingKey) {
      propsCopy.onPointerDown = this.onPointerDown
    }

    return (
      <button ref={forwardedRef} onKeyDown={this.onKeyDown} {...propsCopy} data-testid={dataTestId}>
        {this.props.children}
      </button>
    )
  }
}

// TODO(jmcdonald) could eventually extend this to other components, but as
// of this writing it's only implemented for ButtonPrimary, BigButtonPrimary, and BigButtonSecondary
interface SupportsFullWidth {
  fullWidth?: boolean
}

export interface ButtonPrimaryProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, SafeRecord.KeyProps, SupportsFullWidth {
  dataTestId?: string
}

export const ButtonPrimary = forwardRef<HTMLButtonElement, ButtonPrimaryProps>(
  function ButtonPrimary({ fullWidth, ...props }, ref) {
    return (
      <ButtonBase
        forwardedRef={ref}
        defaultClass={classNames(styles.primaryBtn, fullWidth && styles.fullWidth)}
        {...(props as any)}
      />
    )
  },
)

export const ButtonPrimaryTracked = asTrackedClickable(ButtonPrimary)

export class BigButtonPrimary extends React.Component<
  React.ButtonHTMLAttributes<HTMLButtonElement> & SafeRecord.KeyProps & SupportsFullWidth,
  {}
> {
  public static displayName = 'BigButtonPrimary'

  public override render() {
    const { fullWidth, ...otherProps } = this.props

    return (
      <ButtonBase
        defaultClass={classNames(styles.primaryBtnBig, fullWidth && styles.fullWidth)}
        {...(otherProps as any)}
      />
    )
  }
}

export const BigButtonPrimaryTracked = asTrackedClickable(BigButtonPrimary)

export interface ButtonSecondaryProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, SafeRecord.KeyProps {
  dataTestId?: string
}

/**
 * All non-primary button inputs are given an explicit default type='button' attribute
 * because otherwise browsers will give them type='submit' by default, which can cause
 * the form to "submit" with the wrong button e.g. when you press enter.
 * https://stackoverflow.com/a/41904218
 */
export class ButtonSecondary extends React.Component<ButtonSecondaryProps, {}> {
  public static displayName = 'ButtonSecondary'

  public override render() {
    return <ButtonBase defaultClass={styles.btn} type="button" {...(this.props as any)} />
  }
}

export const ButtonSecondaryTracked = asTrackedClickable(ButtonSecondary)

export class BigButtonSecondary extends React.Component<
  React.ButtonHTMLAttributes<HTMLButtonElement> & SafeRecord.KeyProps & SupportsFullWidth,
  {}
> {
  public static displayName = 'BigButtonSecondary'

  public override render() {
    const { fullWidth, ...otherProps } = this.props

    return (
      <ButtonBase
        defaultClass={classNames(styles.secondaryBtnBig, fullWidth && styles.fullWidth)}
        type="button"
        {...(otherProps as any)}
      />
    )
  }
}

export const BigButtonSecondaryTracked = asTrackedClickable(BigButtonSecondary)

export class ButtonSecondaryNegative extends React.Component<
  React.ButtonHTMLAttributes<HTMLButtonElement> & SafeRecord.KeyProps,
  {}
> {
  public static displayName = 'ButtonSecondaryNegative'

  public override render() {
    return (
      <ButtonBase
        defaultClass={styles.secondaryNegativeBtn}
        type="button"
        {...(this.props as any)}
      />
    )
  }
}

type ButtonNegativeProps = {
  dataTestId?: string
} & React.HTMLProps<HTMLButtonElement> &
  SafeRecord.KeyProps
export class ButtonNegative extends React.Component<ButtonNegativeProps, {}> {
  public static displayName = 'ButtonNegative'

  public override render() {
    return <ButtonBase defaultClass={styles.negativeBtn} type="button" {...(this.props as any)} />
  }
}

export const ButtonNegativeTracked = asTrackedClickable(ButtonNegative)

export class BigButtonNegative extends React.Component<
  React.HTMLProps<HTMLButtonElement> & SafeRecord.KeyProps,
  {}
> {
  public static displayName = 'BigButtonNegative'

  public override render() {
    return (
      <ButtonBase defaultClass={styles.negativeBtnBig} type="button" {...(this.props as any)} />
    )
  }
}

export class ButtonWhite extends React.Component<
  React.HTMLProps<HTMLButtonElement> & SafeRecord.KeyProps,
  {}
> {
  public static displayName = 'ButtonWhite'

  public override render() {
    return <ButtonBase defaultClass={styles.whiteBtn} {...(this.props as any)} />
  }
}
export const ButtonWhiteTracked = asTrackedClickable(ButtonWhite)

export class ButtonInverse extends React.Component<
  React.HTMLProps<HTMLButtonElement> & SafeRecord.KeyProps,
  {}
> {
  public static displayName = 'ButtonInverse'

  public override render() {
    return <ButtonBase defaultClass={styles.inverseBtn} {...(this.props as any)} />
  }
}
export class BigButtonInverse extends React.Component<
  React.HTMLProps<HTMLButtonElement> & SafeRecord.KeyProps & SupportsFullWidth,
  {}
> {
  public static displayName = 'BigButtonInverse'

  public override render() {
    const { fullWidth, ...otherProps } = this.props
    return (
      <ButtonBase
        defaultClass={classNames(styles.inverseBtnBig, fullWidth && styles.fullWidth)}
        {...(otherProps as any)}
      />
    )
  }
}

export const BigButtonInverseTracked = asTrackedClickable(BigButtonInverse)

type ButtonGreenProps = {
  dataTestId?: string
} & React.HTMLProps<HTMLButtonElement> &
  SafeRecord.KeyProps

export class ButtonGreen extends React.Component<ButtonGreenProps, {}> {
  public static displayName = 'ButtonGreen'

  public override render() {
    return <ButtonBase defaultClass={styles.greenBtn} {...(this.props as any)} />
  }
}

type ButtonGreyProps = {
  customClassName?: string
  dataTestId?: string
} & React.HTMLProps<HTMLButtonElement> &
  SafeRecord.KeyProps

export class ButtonGrey extends React.Component<ButtonGreyProps, {}> {
  public static displayName = 'ButtonGrey'

  public override render() {
    const { customClassName, ...otherProps } = this.props

    return (
      <ButtonBase
        defaultClass={styles.greyBtn}
        className={customClassName}
        {...(otherProps as any)}
      />
    )
  }
}

export const ButtonGreenTracked = asTrackedClickable(ButtonGreen)

type BigButtonGreenProps = {
  solidWhenDisabled?: boolean
  dataTestId?: string
} & React.HTMLProps<HTMLButtonElement> &
  SafeRecord.KeyProps

export class BigButtonGreen extends React.Component<BigButtonGreenProps, {}> {
  public static displayName = 'BigButtonGreen'

  public override render() {
    return (
      <ButtonBase
        defaultClass={
          this.props.solidWhenDisabled ? styles.greenBtnBigSolidWhenDisabled : styles.greenBtnBig
        }
        {...(this.props as any)}
      />
    )
  }
}

export class BigButtonBlack extends React.Component<
  React.HTMLProps<HTMLButtonElement> & SafeRecord.KeyProps,
  {}
> {
  public static displayName = 'BigButtonBlack'

  public override render() {
    return <ButtonBase defaultClass={styles.blackBtnBig} {...(this.props as any)} />
  }
}

export const BigButtonBlackTracked = asTrackedClickable(BigButtonBlack)

export class ButtonLink extends React.Component<React.HTMLProps<HTMLAnchorElement>, {}> {
  public static displayName = 'ButtonLink'

  public override render() {
    const className = `${styles.primaryBtn} ${this.props.className || ''}`
    const props = { ...this.props, className, rel: 'noopener' }
    return <a {...props}>{this.props.children}</a>
  }
}

export const ButtonLinkTracked = asTrackedClickable(ButtonLink)

export class BigButtonLink extends React.Component<
  React.HTMLProps<HTMLAnchorElement> & SupportsFullWidth,
  {}
> {
  public static displayName = 'ButtonLink'

  public override render() {
    let { fullWidth, ...props } = this.props
    const defaultClass = classNames(styles.primaryBtnBig, this.props.fullWidth && styles.fullWidth)
    const className = `${defaultClass} ${this.props.className || ''}`
    props = { ...props, className, rel: 'noopener' }
    return <a {...props}>{this.props.children}</a>
  }
}

export const BigButtonLinkTracked = asTrackedClickable(BigButtonLink)

export class ButtonSecondaryLink extends React.Component<React.HTMLProps<HTMLAnchorElement>, {}> {
  public static displayName = 'ButtonSecondaryLink'

  public override render() {
    const className = `${styles.btn} ${this.props.className || ''}`
    const props = { ...this.props, className, rel: 'noopener' }
    return <a {...props}>{this.props.children}</a>
  }
}

export const ButtonSecondaryLinkTracked = asTrackedClickable(ButtonSecondaryLink)

export class BigButtonSecondaryLink extends React.Component<
  React.HTMLProps<HTMLAnchorElement>,
  {}
> {
  public static displayName = 'BigButtonSecondaryLink'

  public override render() {
    const className = `${styles.btnBig} ${this.props.className || ''}`
    const props = { ...this.props, className, rel: 'noopener' }
    return <a {...props}>{this.props.children}</a>
  }
}

export const WORKSPACE_AND_BILLING_GROUP_NAME_CHAR_LIMIT = 255
export const FOLDER_NAME_CHAR_LIMIT = 118

export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  dataTestId?: string
  recordingKey?: string
}

export const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  { className, ...props },
  ref,
) {
  return (
    <LazyInput__DEPRECATED
      className={classNames(styles.textInput, className)}
      {...props}
      ref={ref}
    />
  )
})

export function MockTextInput(props: TextInputProps) {
  return <input {...props} />
}

export class BigTextInput extends React.Component<React.HTMLProps<HTMLInputElement>, {}> {
  public static displayName = 'BigTextInput'

  public override render() {
    const className = `${styles.bigTextInput} ${this.props.className || ''}`
    const props = { ...this.props, className }
    return <LazyInput__DEPRECATED {...(props as any)} />
  }
}

const LazyInputTracked = asTrackedInput(LazyInput__DEPRECATED)

type LabeledTextInputProps = {
  value: string
  htmlName: string
  label: string
  onKeyPress?: (event: React.KeyboardEvent<HTMLInputElement>) => void
  onChange: (e: React.SyntheticEvent) => void
  onBlur?: (e: React.SyntheticEvent) => void
  onFocus?: (e: React.SyntheticEvent) => void
  placeholder?: string
  trackingFieldName?: string
  autoCompleteName?: string
  maxLength?: number
  disabled?: boolean
  className?: string
  type?: string
  dataTestId?: string
  required?: boolean
  tooltip?: string
  hasError?: boolean
  noMarginBottom?: boolean
}

export class LabeledTextInput extends React.Component<LabeledTextInputProps, {}> {
  public override render() {
    return (
      <div
        className={
          this.props.className ||
          (this.props.disabled ? styles.labeledInputGroupDisabled : styles.labeledInputGroup)
        }
        style={sx_DEPRECATED.if(this.props.noMarginBottom, { marginBottom: 0 }).$}
      >
        <label
          htmlFor={this.props.htmlName}
          className={classNames(styles.hoverLabel, {
            [styles.hoverLabel__hasValue]: this.props.value,
            [styles.error]: this.props.hasError,
          })}
        >
          {this.props.label}
        </label>
        {this.props.trackingFieldName && (
          <LazyInputTracked
            autoComplete={this.props.autoCompleteName}
            dataTestId={this.props.dataTestId}
            disabled={this.props.disabled}
            id={this.props.htmlName}
            maxLength={this.props.maxLength || 50}
            name={this.props.htmlName}
            onBlur={this.props.onBlur}
            onChange={this.props.onChange}
            onFocus={this.props.onFocus}
            onKeyPress={this.props.onKeyPress}
            placeholder={this.props.placeholder || this.props.label}
            required={this.props.required}
            trackingFieldName={this.props.trackingFieldName}
            trackingPopulationLevel={
              this.props.value
                ? InputPopulationLevel.POPULATED_COMPLETE
                : InputPopulationLevel.NOT_POPULATED
            }
            type={this.props.type || 'text'}
            value={this.props.value}
          />
        )}
        {!this.props.trackingFieldName && (
          <LazyInput__DEPRECATED
            autoComplete={this.props.autoCompleteName}
            dataTestId={this.props.dataTestId}
            disabled={this.props.disabled}
            id={this.props.htmlName}
            maxLength={this.props.maxLength || 50}
            name={this.props.htmlName}
            onBlur={this.props.onBlur}
            onChange={this.props.onChange}
            onFocus={this.props.onFocus}
            onKeyPress={this.props.onKeyPress}
            placeholder={this.props.placeholder || this.props.label}
            required={this.props.required}
            type={this.props.type || 'text'}
            value={this.props.value}
          />
        )}
        {this.props.tooltip && (
          <Svg
            className={styles.infoIcon}
            svg={infoIcon}
            {...getTooltipProps({
              type: TooltipType.TEXT,
              tooltip: this.props.tooltip,
              timeoutDelay: 300,
            })}
          />
        )}
      </div>
    )
  }
}

export function Label(props: React.HTMLProps<HTMLLabelElement>) {
  const className = `${styles.label} ${props.className || ''}`
  return <label {...props} className={className} />
}

export const Link = BaseLink

export const LinkTracked = asTrackedClickable(Link)

export class LinkInline extends React.Component<LinkProps> {
  public static displayName = 'Link'

  public override render() {
    const { canFocus, trusted, className: classNameProp, ...otherProps } = this.props
    const className = `${styles.inlineLink} ${classNameProp || ''}`
    let props: React.HTMLProps<HTMLAnchorElement> = {
      ...otherProps,
      className,
      // For non-trusted content (e.g., user-generated), we add additional tags,
      // in part to preserve our link reputation for crawlers (e.g., Google)
      rel: trusted ? 'noopener' : 'noopener nofollow noreferrer ugc',
      onClick: (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (this.props.onClick) {
          this.props.onClick(e)
        }
        if (this.props.href) {
          tryOpenUrlForIntegration(this.props.href, e)
        }
      },
    }

    if (canFocus) {
      props = {
        href: '#',
        tabIndex: 0,
        ...props,
      }
    }

    return <a {...props}>{this.props.children}</a>
  }
}

export const LinkInlineTracked = asTrackedClickable(LinkInline)

export class BigSelect extends React.Component<React.HTMLProps<HTMLSelectElement>, {}> {
  public static displayName = 'BigSelect'

  public override render() {
    const className = `${styles.bigSelect} ${this.props.className || ''}`
    const props = { ...this.props, className }
    return <select {...props} />
  }
}

export function Spacing(props: { direction?: 'x' | 'y'; value?: number; multiple?: number }) {
  const spacingValue = props.multiple ? 8 * props.multiple : props.value ? props.value : 0

  return props.direction === 'x' ? (
    <div
      {...stylex.props(styleXStyles.spacing, styleXStyles.horizontalSpacing(spacingValue))}
      aria-hidden="true"
    />
  ) : (
    <div
      {...stylex.props(styleXStyles.spacing, styleXStyles.verticalSpacing(spacingValue))}
      aria-hidden="true"
    />
  )
}
Spacing.displayName = 'Spacing'

// This is here because a disallowed dependency cycle was created
// when exporting directly from the svg.tsx
export const SvgTracked = asTrackedClickable(Svg)

export const AnchorElementTracked = asTrackedClickable(
  (props: React.HTMLProps<HTMLAnchorElement>) => <a {...props}>{props.children}</a>,
)

type ButtonTextProps = {
  dataTestId?: string
} & React.HTMLProps<HTMLButtonElement> &
  SafeRecord.KeyProps

export function ButtonText(props: ButtonTextProps) {
  return (
    <ButtonBase defaultClass={classNames(styles.buttonText, props.className)} {...(props as any)} />
  )
}

export const ButtonTextTracked = asTrackedClickable(ButtonText)

const styleXStyles = stylex.create({
  spacing: { flexShrink: 0 },
  horizontalSpacing: (width: number) => ({ width }),
  verticalSpacing: (height: number) => ({ height }),
})
